import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  AdminBusinessLineAssignmentPatchRequest,
  AdminBusinessLineAssignmentPatchResponse,
  AdminBusinessLineAssignmentsResponse,
  AdminEngineLifecycleStatusResponse,
  AdminLocalProcessorSettingsResponse,
  AdminMonitoringPermissionPatchRequest,
  AdminOracordEnginePairingCodeResponse,
  AdminMonitoringPermissionsResponse,
  AdminStaffProfileSetupCompletenessResponse,
  AdminStaffProfilesResponse,
  MonitoringPermissionChannel,
} from '@oracord/shared';
import { apiFetch } from '../lib/api';
import { requestOracordEnginePairingCode } from '../lib/oracord-engine-pairing';
import { engineSetupLink, engineStartServiceLink } from '../lib/oracord-engine-setup-command';
import { API_BASE_URL } from '../lib/config';
import { describeOracordEngineState } from '../lib/oracord-engine-state';
import { useMe } from '../hooks/useMe';
import { useSessionStore } from '../stores/session';

type SettingsTab = 'setup' | 'oracord' | 'reporting';
type ToggleTone = 'blue' | 'slate';

type MonitoringPatchResponse = {
  row: AdminMonitoringPermissionsResponse['rows'][number];
  privacy: AdminMonitoringPermissionsResponse['privacy'];
};

const SETTINGS_TABS: Array<{ key: SettingsTab; label: string }> = [
  { key: 'setup', label: 'Connections' },
  { key: 'oracord', label: 'Oracord' },
  { key: 'reporting', label: 'Reporting' },
];

const SOURCE_ROWS: Array<{
  channel: MonitoringPermissionChannel;
  title: string;
  description: string;
}> = [
  { channel: 'inbound_calls', title: 'Include incoming calls', description: 'Use calls from customers.' },
  { channel: 'outbound_calls', title: 'Include outgoing calls', description: 'Use calls from your team.' },
  { channel: 'missed_calls', title: 'Include missed calls', description: 'Show calls that may need a reply.' },
  { channel: 'sms', title: 'Include text messages', description: 'Use business SMS conversations.' },
  { channel: 'email', title: 'Include email', description: 'Use business email conversations.' },
  { channel: 'calendar', title: 'Include calendar events', description: 'Use appointments and meetings.' },
];

const TRANSCRIPT_SOURCE_ROW = {
  title: 'Create call transcripts',
  description: 'Turn call audio into written transcripts for Oracord to summarize.',
};

const ORACORD_ROWS = [
  { title: 'Summarize transcripts', description: 'Create short notes from written call transcripts.', defaultOn: true },
  { title: 'Find contact details', description: 'Identify names, phone numbers, and addresses.', defaultOn: false },
  { title: 'Generate coaching suggestions', description: 'Suggest helpful follow-up and communication tips.', defaultOn: true },
  { title: 'Find missed follow-ups', description: 'Flag customers who may need a reply.', defaultOn: true },
];

const ENGINE_LIFECYCLE_LABELS: Record<AdminEngineLifecycleStatusResponse['rows'][number]['key'], string> = {
  calls_received: 'Calls are being received',
  recordings_saved: 'Recordings are being saved',
  transcripts_ready: 'Transcripts are reaching Oracord Engine',
  engine_results_received: 'Processing results received',
  audio_cleanup_scheduled: 'Audio cleanup scheduled',
};

const RETENTION_ROWS = [
  { title: 'Call audio', description: 'Keep audio until the transcript is ready.', value: 'Until transcript is ready' },
  { title: 'Transcripts', description: 'Keep written transcripts available.', value: 'Keep' },
];

const VOIP_PROVIDER = 'Telus Business Connect';

function normalizeSettingsTab(value: string | null): SettingsTab {
  if (value === 'oracord' || value === 'reporting') return value;
  return 'setup';
}

export default function Settings() {
  const { me, loading, error } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const active = me?.organizations.find(org => org.id === activeOrgId);
  const canManage = active?.role === 'owner' || active?.role === 'admin';
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => normalizeSettingsTab(searchParams.get('tab')));
  const [monitoring, setMonitoring] = useState<AdminMonitoringPermissionsResponse | null>(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringError, setMonitoringError] = useState<string | null>(null);
  const [localProcessor, setLocalProcessor] = useState<AdminLocalProcessorSettingsResponse | null>(null);
  const [localProcessorLoading, setLocalProcessorLoading] = useState(false);
  const [localProcessorError, setLocalProcessorError] = useState<string | null>(null);
  const [engineLifecycle, setEngineLifecycle] = useState<AdminEngineLifecycleStatusResponse | null>(null);
  const [engineLifecycleLoading, setEngineLifecycleLoading] = useState(false);
  const [engineLifecycleError, setEngineLifecycleError] = useState<string | null>(null);
  const [businessLines, setBusinessLines] = useState<AdminBusinessLineAssignmentsResponse | null>(null);
  const [staffProfiles, setStaffProfiles] = useState<AdminStaffProfilesResponse['rows']>([]);
  const [businessLinesLoading, setBusinessLinesLoading] = useState(false);
  const [businessLinesError, setBusinessLinesError] = useState<string | null>(null);
  const [pendingBusinessLine, setPendingBusinessLine] = useState<string | null>(null);
  const [enginePairingCode, setEnginePairingCode] = useState<AdminOracordEnginePairingCodeResponse | null>(null);
  const [enginePairingLoading, setEnginePairingLoading] = useState(false);
  const [enginePairingError, setEnginePairingError] = useState<string | null>(null);
  const [pendingChannel, setPendingChannel] = useState<MonitoringPermissionChannel | null>(null);
  const [reportRecipients, setReportRecipients] = useState('');
  const [aiToggles, setAiToggles] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(ORACORD_ROWS.map(row => [row.title, row.defaultOn])),
  );

  useEffect(() => {
    setActiveTab(normalizeSettingsTab(searchParams.get('tab')));
  }, [searchParams]);

  const selectTab = useCallback((tab: SettingsTab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'setup' ? { tab: 'connections' } : { tab });
  }, [setSearchParams]);

  const loadMonitoring = useCallback(async () => {
    if (!me || !active || !canManage) return;
    setMonitoringLoading(true);
    setMonitoringError(null);
    try {
      const result = await apiFetch<AdminMonitoringPermissionsResponse>('/api/admin/settings/monitoring-permissions');
      setMonitoring(result);
    } catch (err) {
      setMonitoringError(err instanceof Error ? err.message : 'Settings could not be loaded.');
    } finally {
      setMonitoringLoading(false);
    }
  }, [active, canManage, me]);

  const loadLocalProcessor = useCallback(async () => {
    if (!me || !active || !canManage) return;
    setLocalProcessorLoading(true);
    setLocalProcessorError(null);
    try {
      const result = await apiFetch<AdminLocalProcessorSettingsResponse>('/api/admin/settings/local-processor');
      setLocalProcessor(result);
    } catch (err) {
      setLocalProcessorError(err instanceof Error ? err.message : 'Oracord Engine settings could not be loaded.');
    } finally {
      setLocalProcessorLoading(false);
    }
  }, [active, canManage, me]);

  const loadEngineLifecycle = useCallback(async () => {
    if (!me || !active || !canManage) return;
    setEngineLifecycleLoading(true);
    setEngineLifecycleError(null);
    try {
      const result = await apiFetch<AdminEngineLifecycleStatusResponse>('/api/admin/settings/engine-lifecycle-status');
      setEngineLifecycle(result);
    } catch (err) {
      setEngineLifecycleError(err instanceof Error ? err.message : 'Processing lifecycle could not be loaded.');
    } finally {
      setEngineLifecycleLoading(false);
    }
  }, [active, canManage, me]);

  const loadBusinessLines = useCallback(async () => {
    if (!me || !active || !canManage) return;
    setBusinessLinesLoading(true);
    setBusinessLinesError(null);
    try {
      const [assignments, staff] = await Promise.all([
        apiFetch<AdminBusinessLineAssignmentsResponse>('/api/admin/business-line-assignments'),
        apiFetch<AdminStaffProfilesResponse>('/api/admin/staff'),
      ]);
      setBusinessLines(assignments);
      setStaffProfiles(staff.rows);
    } catch (err) {
      setBusinessLinesError(err instanceof Error ? err.message : 'Business line assignments could not be loaded.');
    } finally {
      setBusinessLinesLoading(false);
    }
  }, [active, canManage, me]);

  useEffect(() => {
    void loadMonitoring();
    void loadLocalProcessor();
    void loadEngineLifecycle();
    void loadBusinessLines();
  }, [loadMonitoring, loadLocalProcessor, loadEngineLifecycle, loadBusinessLines]);

  const monitoringByChannel = useMemo(() => {
    const map = new Map<MonitoringPermissionChannel, AdminMonitoringPermissionsResponse['rows'][number]>();
    for (const row of monitoring?.rows ?? []) map.set(row.channel, row);
    return map;
  }, [monitoring]);

  async function setSource(channel: MonitoringPermissionChannel, allowed: boolean) {
    if (!canManage) return;
    setPendingChannel(channel);
    setMonitoringError(null);
    try {
      const body: AdminMonitoringPermissionPatchRequest = { channel, allowed };
      const result = await apiFetch<MonitoringPatchResponse>('/api/admin/settings/monitoring-permissions', {
        method: 'PATCH',
        body,
      });
      setMonitoring(current => {
        if (!current) return { rows: [result.row], privacy: result.privacy };
        const rows = current.rows.some(row => row.channel === result.row.channel)
          ? current.rows.map(row => (row.channel === result.row.channel ? result.row : row))
          : [...current.rows, result.row];
        return { rows, privacy: result.privacy };
      });
    } catch (err) {
      setMonitoringError(err instanceof Error ? err.message : 'Setting could not be saved.');
    } finally {
      setPendingChannel(null);
    }
  }

  async function requestEnginePairingCode() {
    if (!canManage) return;
    setEnginePairingLoading(true);
    setEnginePairingError(null);
    try {
      const result = await requestOracordEnginePairingCode();
      setEnginePairingCode(result);
    } catch (err) {
      setEnginePairingError(err instanceof Error ? err.message : 'Pairing code could not be created.');
    } finally {
      setEnginePairingLoading(false);
    }
  }

  async function assignBusinessLine(phoneNumber: string, staffProfileId: string | null) {
    if (!canManage) return;
    setPendingBusinessLine(phoneNumber);
    setBusinessLinesError(null);
    try {
      const body: AdminBusinessLineAssignmentPatchRequest = { phoneNumber, staffProfileId };
      const result = await apiFetch<AdminBusinessLineAssignmentPatchResponse>('/api/admin/business-line-assignments', {
        method: 'PATCH',
        body,
      });
      setBusinessLines(current => {
        const base = current ?? {
          organizationId: result.organizationId,
          rows: [],
          sharedLineSupport: result.sharedLineSupport,
          sourceHonesty: result.sourceHonesty,
        };
        return {
          ...base,
          rows: base.rows.map(row => (row.phoneNumber === result.row.phoneNumber ? result.row : row)),
        };
      });
    } catch (err) {
      setBusinessLinesError(err instanceof Error ? err.message : 'Assignment could not be saved.');
    } finally {
      setPendingBusinessLine(null);
    }
  }

  if (loading && !me) return <SettingsShell><Card title="Settings"><p className="text-sm text-slate-500">Loading…</p></Card></SettingsShell>;
  if (error && !me) return <SettingsShell><Card title="Settings"><p className="text-sm text-red-600">{error.message}</p></Card></SettingsShell>;
  if (!me) return <SettingsShell><Card title="Settings"><p className="text-sm text-slate-500">Settings are unavailable.</p></Card></SettingsShell>;

  return (
    <SettingsShell>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Settings</h1>

      <div className="flex flex-wrap gap-2">
        {SETTINGS_TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => selectTab(tab.key)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              activeTab === tab.key
                ? 'bg-slate-950 text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!active && (
        <Card title="Account">
          <p className="text-sm text-slate-500">Choose an organization before changing settings.</p>
        </Card>
      )}

      {active && activeTab === 'setup' && (
        <div className="space-y-5">
          <Card
            title="Account"
            right={<StatusText tone="slate">Not connected</StatusText>}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-slate-950">Business phone system</p>
                <p className="mt-1 text-sm text-slate-500">Choose the VoIP system Oracord will connect to.</p>
              </div>
              <select
                value={VOIP_PROVIDER}
                onChange={() => undefined}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 outline-none transition hover:border-slate-300 hover:bg-white focus:border-slate-400"
                aria-label="VoIP provider"
              >
                <option>{VOIP_PROVIDER}</option>
              </select>
            </div>
          </Card>

          <Card
            title="Business Lines"
            description="Numbers your staff use for business calls and messages"
            right={<QuietAction>Buy business number</QuietAction>}
          >
            <BusinessLineAssignmentsCard
              assignments={businessLines}
              staffProfiles={staffProfiles}
              loading={businessLinesLoading}
              error={businessLinesError}
              canManage={canManage}
              pendingPhoneNumber={pendingBusinessLine}
              onAssign={(phoneNumber, staffProfileId) => void assignBusinessLine(phoneNumber, staffProfileId)}
            />
          </Card>

          <Card
            title="Email"
            description="Add Gmail accounts for Oracord"
            right={<QuietAction>Connect Gmail</QuietAction>}
          >
            <ConnectedList rows={[]} empty="No email accounts connected yet" />
          </Card>

          <Card
            title="Calendars"
            description="Connect calendars for appointments and meeting outcomes"
            right={<QuietAction>Connect calendar</QuietAction>}
          >
            <ConnectedList rows={[]} empty="No calendars connected yet" />
          </Card>

          <Card title="Sources" description="Choose what Oracord can use">
            {monitoringError && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{monitoringError}</p>}
            <div className="divide-y divide-slate-100">
              <InfoRow title={TRANSCRIPT_SOURCE_ROW.title} description={TRANSCRIPT_SOURCE_ROW.description} />
              {SOURCE_ROWS.map(row => {
                const current = monitoringByChannel.get(row.channel);
                const checked = current?.allowed ?? false;
                return (
                  <SettingToggleRow
                    key={row.channel}
                    title={row.title}
                    description={row.description}
                    checked={checked}
                    disabled={!canManage || monitoringLoading || pendingChannel === row.channel}
                    onChange={value => void setSource(row.channel, value)}
                  />
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {active && activeTab === 'oracord' && (
        <div className="space-y-5">
          <OracordEngineCard
            processor={localProcessor}
            loading={localProcessorLoading}
            error={localProcessorError}
            pairingCode={enginePairingCode}
            pairingLoading={enginePairingLoading}
            pairingError={enginePairingError}
            canManage={canManage}
            onRequestPairingCode={() => void requestEnginePairingCode()}
          />

          <ProcessingLifecycleCard
            lifecycle={engineLifecycle}
            loading={engineLifecycleLoading}
            error={engineLifecycleError}
          />

          <Card title="Oracord" description="Choose the help Oracord can provide">
            <div className="divide-y divide-slate-100">
              {ORACORD_ROWS.map(row => (
                <SettingToggleRow
                  key={row.title}
                  title={row.title}
                  description={row.description}
                  checked={aiToggles[row.title] ?? false}
                  onChange={value => setAiToggles(current => ({ ...current, [row.title]: value }))}
                />
              ))}
            </div>
          </Card>

          <Card title="Retention" description="Choose how long Oracord keeps files">
            <div className="divide-y divide-slate-100">
              {RETENTION_ROWS.map(row => (
                <SelectRow key={row.title} title={row.title} description={row.description} value={row.value} />
              ))}
            </div>
          </Card>
        </div>
      )}

      {active && activeTab === 'reporting' && (
        <div className="space-y-5">
          <Card title="Reports" description="Set up simple manager email reports">
            <div className="space-y-4">
              <SelectRow title="Email reports" description="Send a regular summary to managers" value="Weekly" />
              <InputRow
                id="sample-report-recipients"
                title="Sample recipients"
                description="Comma-separated email addresses for this preview"
                value={reportRecipients}
                placeholder="manager@example.com, owner@example.com"
                onChange={setReportRecipients}
              />
              <button type="button" className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-blue-200 hover:text-blue-700">
                Preview sample report
              </button>
            </div>
          </Card>
        </div>
      )}
    </SettingsShell>
  );
}

function SettingsShell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-5xl space-y-6 pb-12">{children}</div>;
}

function Card({
  title,
  description,
  right,
  children,
}: {
  title: string;
  description?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm shadow-slate-200/70">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function StaffSetupCompletenessCard({
  setup,
  loading,
  error,
}: {
  setup: AdminStaffProfileSetupCompletenessResponse | null;
  loading: boolean;
  error: string | null;
}) {
  const summary = setup?.summary;
  const readyText = summary
    ? `${summary.readyForCurrentSetup} of ${summary.activeProfiles} active staff profiles ready for current setup.`
    : 'Checking staff setup…';

  return (
    <Card
      title="Staff setup"
      description="Business line, email, and calendar setup status for active staff profiles"
      right={summary ? <StaffSetupSummaryPill ready={summary.readyForCurrentSetup} total={summary.activeProfiles} /> : undefined}
    >
      {error && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {loading && !setup && <p className="text-sm text-slate-500">Loading staff setup…</p>}
      <p className="text-sm text-slate-600">{readyText}</p>

      {summary && (
        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <StaffSetupMetric label="Ready" value={summary.readyForCurrentSetup} tone="green" />
          <StaffSetupMetric label="Needs line" value={summary.needsBusinessLine} tone="amber" />
          <StaffSetupMetric label="Needs email" value={summary.needsWorkEmail} tone="amber" />
          <StaffSetupMetric label="Needs Google" value={summary.needsGoogleWorkspace} tone={setup.googleWorkspace.configured ? 'amber' : 'slate'} />
        </div>
      )}

      {setup?.rows.length ? (
        <div className="mt-4 divide-y divide-slate-100 rounded-2xl border border-slate-100 bg-slate-50/50 px-4">
          {setup.rows.map(row => (
            <div key={row.staffProfileId} className="py-4 first:pt-4 last:pb-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-950">{row.label}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {row.missing.length === 0 ? 'Current setup pieces are in place.' : staffSetupMissingText(row.missing)}
                  </p>
                </div>
                <StaffSetupRowPill complete={row.missing.length === 0} />
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <StaffSetupCheck label="Phone line" check={row.checks.businessLine} />
                <StaffSetupCheck label="Work email" check={row.checks.workEmail} />
                <StaffSetupCheck label="Google" check={row.checks.googleWorkspace} />
              </div>
              <StaffGoogleWorkspaceIngestionSummary ingestion={row.googleWorkspaceIngestion} />
            </div>
          ))}
        </div>
      ) : setup && !loading ? (
        <EmptyPanel title="No active staff profiles" description="Create staff profiles before checking staff setup completeness." />
      ) : null}

      {setup && (
        <div className="mt-4 space-y-1 text-xs text-slate-400">
          <p>
            Google status reflects saved permission and configuration records only; it does not prove live Gmail or calendar ingestion.
          </p>
          <p>
            This view shows setup health only. It does not show phone numbers, private coaching, transcripts, Gmail messages, calendar event details,
            scores, or rankings.
          </p>
        </div>
      )}
    </Card>
  );
}

function StaffSetupSummaryPill({ ready, total }: { ready: number; total: number }) {
  const allReady = total > 0 && ready === total;
  return (
    <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${allReady ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
      {allReady ? 'Ready' : 'Needs setup'}
    </span>
  );
}

function StaffSetupMetric({ label, value, tone }: { label: string; value: number; tone: 'green' | 'amber' | 'slate' }) {
  const classes = {
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    slate: 'bg-slate-50 text-slate-500',
  }[tone];
  return (
    <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${classes}`}>{value}</div>
    </div>
  );
}

function StaffSetupCheck({
  label,
  check,
}: {
  label: string;
  check: AdminStaffProfileSetupCompletenessResponse['rows'][number]['checks']['businessLine'];
}) {
  return (
    <div className="rounded-xl bg-white px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-800">{label}</span>
        <StaffSetupCheckPill status={check.status} />
      </div>
      <p className="mt-1 text-xs text-slate-500">{check.label}</p>
    </div>
  );
}

function StaffSetupCheckPill({ status }: { status: AdminStaffProfileSetupCompletenessResponse['rows'][number]['checks']['businessLine']['status'] }) {
  const meta = {
    ready: { label: 'Ready', className: 'bg-emerald-50 text-emerald-700' },
    needs_setup: { label: 'Needs setup', className: 'bg-amber-50 text-amber-700' },
    not_supported: { label: 'Unavailable', className: 'bg-slate-100 text-slate-600' },
  }[status];
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.className}`}>{meta.label}</span>;
}

function StaffGoogleWorkspaceIngestionSummary({
  ingestion,
}: {
  ingestion: AdminStaffProfileSetupCompletenessResponse['rows'][number]['googleWorkspaceIngestion'];
}) {
  const statusItems = [
    { label: 'Invited', active: ingestion.invited || ingestion.accepted || ingestion.googleMatched },
    { label: 'Accepted', active: ingestion.accepted || ingestion.googleMatched },
    { label: 'Google matched', active: ingestion.googleMatched },
    { label: 'Gmail connected', active: ingestion.gmailConnected },
    { label: 'Calendar connected', active: ingestion.calendarConnected },
    { label: ingestion.ingestionActive ? 'Ingestion active' : 'Ingestion inactive', active: ingestion.ingestionActive },
  ];

  return (
    <div className="mt-3 rounded-xl border border-slate-100 bg-white px-3 py-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Google Workspace ingestion</p>
        <p className="text-xs text-slate-500">{ingestion.lastSyncedAt ? `Last synced ${formatStaffSetupTimestamp(ingestion.lastSyncedAt)}` : 'No sync recorded'}</p>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {statusItems.map(item => (
          <span
            key={item.label}
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${item.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
          >
            {item.label}
          </span>
        ))}
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <StaffWorkspaceChannelStatus label="Gmail" channel={ingestion.gmail} connected={ingestion.gmailConnected} />
        <StaffWorkspaceChannelStatus label="Calendar" channel={ingestion.calendar} connected={ingestion.calendarConnected} />
      </div>
      <p className="mt-2 text-xs text-slate-400">{ingestion.sourceHonesty}</p>
    </div>
  );
}

function StaffWorkspaceChannelStatus({
  label,
  channel,
  connected,
}: {
  label: string;
  channel:
    | AdminStaffProfileSetupCompletenessResponse['rows'][number]['googleWorkspaceIngestion']['gmail']
    | AdminStaffProfileSetupCompletenessResponse['rows'][number]['googleWorkspaceIngestion']['calendar'];
  connected: boolean;
}) {
  const text = connected
    ? 'Connected'
    : channel.credentialReady
      ? 'Credential ready'
      : channel.livePollingAvailable
        ? 'Live polling available'
        : staffSetupStatusLabel(channel.status);
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-700">{label}</span>
        <span className={connected || channel.credentialReady ? 'text-emerald-700' : 'text-slate-500'}>{text}</span>
      </div>
      <p className="mt-1 text-slate-400">{channel.lastSyncedAt ? `Synced ${formatStaffSetupTimestamp(channel.lastSyncedAt)}` : 'No channel sync recorded'}</p>
    </div>
  );
}

function staffSetupStatusLabel(value: string) {
  return value.replace(/_/g, ' ');
}

function formatStaffSetupTimestamp(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function StaffSetupRowPill({ complete }: { complete: boolean }) {
  return (
    <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${complete ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
      {complete ? 'Complete' : 'Needs setup'}
    </span>
  );
}

function staffSetupMissingText(missing: AdminStaffProfileSetupCompletenessResponse['rows'][number]['missing']) {
  const labels: Record<AdminStaffProfileSetupCompletenessResponse['rows'][number]['missing'][number], string> = {
    business_line: 'business line assignment',
    work_email: 'work email',
    google_workspace: 'Google authorization',
  };
  return `Needs ${missing.map(key => labels[key]).join(', ')}.`;
}

function OracordEngineCard({
  processor,
  loading,
  error,
  pairingCode,
  pairingLoading,
  pairingError,
  canManage,
  onRequestPairingCode,
}: {
  processor: AdminLocalProcessorSettingsResponse | null;
  loading: boolean;
  error: string | null;
  pairingCode: AdminOracordEnginePairingCodeResponse | null;
  pairingLoading: boolean;
  pairingError: string | null;
  canManage: boolean;
  onRequestPairingCode: () => void;
}) {
  const engine = processor?.engine;
  const engineState = describeOracordEngineState(processor);
  const canDownload = Boolean(engine?.downloadPath || engine?.downloadAction);
  const downloadButtonLabel = engine?.downloadArtifactStatus === 'unsigned_development_pkg' ? 'Download Mac pkg' : 'Download for Mac';
  const setupLink = pairingCode ? engineSetupLink({ pairingCode: pairingCode.pairingCode, serverUrl: API_BASE_URL }) : null;
  const engineStartLink = engineStartServiceLink();
  const needsServiceStart = engine?.backgroundService && engine.backgroundService.status !== 'running';

  return (
    <Card
      title="Oracord Engine"
      description="Keeps Oracord work on the local machine"
      right={engine ? <EngineStatusPill status={engine.status} label={engine.statusLabel} /> : undefined}
    >
      {error && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {pairingError && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{pairingError}</p>}
      {loading && !engine && <p className="text-sm text-slate-500">Loading Oracord Engine setup…</p>}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-slate-600">
            {engine?.localProcessingMessage ?? 'Oracord Engine keeps processing and storage local/private on this machine.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <EngineStatePill label={engineState.connectionLabel} tone={engineState.connectionTone} />
            <EngineStatePill label={engineState.readinessLabel} tone={engineState.readinessTone} />
          </div>
          <p className="mt-3 text-sm font-medium text-slate-700">{engineState.versionLabel}</p>
          <p className="mt-1 text-xs text-slate-500">{engineState.lastSeenLabel}</p>
          {engine?.heartbeat && engine.backgroundService ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <EngineGuidancePanel
                title="Heartbeat"
                label={engine.heartbeat.statusLabel}
                tone={engine.heartbeat.status === 'fresh' ? 'green' : engine.heartbeat.status === 'stale' ? 'amber' : 'slate'}
                detail={engine.heartbeat.guidance}
              />
              <EngineGuidancePanel
                title="Background service"
                label={engine.backgroundService.statusLabel}
                tone={engine.backgroundService.status === 'running' ? 'green' : engine.backgroundService.status === 'recommended' ? 'amber' : 'slate'}
                detail={engine.backgroundService.guidance}
              />
              {engine.backlog ? (
                <EngineGuidancePanel
                  title="Backlog"
                  label={engine.backlog.statusLabel}
                  tone={engine.backlog.pendingProcessing > 0 ? 'amber' : 'green'}
                  detail={engine.backlog.guidance}
                />
              ) : null}
            </div>
          ) : null}
          {needsServiceStart ? (
            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
              <p className="text-sm font-semibold text-amber-950">Start Engine on this Mac</p>
              <p className="mt-1 text-xs leading-5 text-amber-800">
                Click Start Engine and approve the Oracord Engine popup. The local helper starts the explicit LaunchAgent heartbeat loop without asking the admin to use Terminal.
              </p>
              <a
                href={engineStartLink}
                className="mt-3 inline-flex items-center justify-center rounded-xl bg-amber-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-800"
              >
                Start Engine
              </a>
              <p className="mt-2 text-xs leading-5 text-amber-800">
                After approving the popup, wait about a minute and refresh this status.
              </p>
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:items-end">
          {engine?.downloadPath ? (
            <a
              href={engine.downloadPath}
              download
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-white hover:text-slate-950"
            >
              {downloadButtonLabel}
            </a>
          ) : (
            <button
              type="button"
              disabled={!canDownload}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-white hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloadButtonLabel}
            </button>
          )}
          {engine?.downloadArtifactStatus === 'unsigned_development_pkg' && engine.downloadFallbackPath ? (
            <a
              href={engine.downloadFallbackPath}
              download
              className="text-xs font-medium text-slate-500 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-700"
            >
              Support fallback: .command installer
            </a>
          ) : null}
          <button
            type="button"
            disabled={!canManage || pairingLoading}
            onClick={onRequestPairingCode}
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pairingLoading ? 'Creating link…' : 'Create setup link'}
          </button>
        </div>
      </div>
      {pairingCode && (
        <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
          <p className="text-sm font-medium text-blue-950">Setup link ready for Oracord Engine.</p>
          <p className="mt-1 text-xs leading-5 text-blue-800">
            Open the setup link on this Mac, approve the Oracord Engine popup, then return here to confirm Engine status and setup checks.
          </p>
          {setupLink && (
            <a
              href={setupLink}
              className="mt-3 inline-flex items-center justify-center rounded-xl bg-blue-950 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-900"
            >
              Open Engine setup link
            </a>
          )}
          <p className="mt-3 text-xs text-blue-800">This setup link expires {formatPairingExpiry(pairingCode.expiresAt)}.</p>
        </div>
      )}
      {engine?.backlog ? (
        <div className="mt-4 grid gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 sm:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Recordings queued</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{engine.backlog.pendingRecordings}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Transcripts queued</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{engine.backlog.pendingTranscripts}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Runtime status</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">{engine.backlog.statusLabel}</p>
          </div>
        </div>
      ) : null}
      {engine?.checks.length ? (
        <div className="mt-4 divide-y divide-slate-100">
          {engine.checks.map(check => (
            <div key={check.label} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
              <span className="text-sm font-medium text-slate-800">{check.label}</span>
              <EngineCheckPill status={check.status} />
            </div>
          ))}
        </div>
      ) : null}
      {engine && !canDownload && (
        <p className="mt-4 text-xs text-slate-400">Download is not available yet.</p>
      )}
    </Card>
  );
}

function EngineGuidancePanel({
  title,
  label,
  tone,
  detail,
}: {
  title: string;
  label: string;
  tone: 'green' | 'amber' | 'slate';
  detail: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{title}</span>
        <EngineStatePill label={label} tone={tone} />
      </div>
      <p className="mt-2 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

function ProcessingLifecycleCard({
  lifecycle,
  loading,
  error,
}: {
  lifecycle: AdminEngineLifecycleStatusResponse | null;
  loading: boolean;
  error: string | null;
}) {
  return (
    <Card
      title="Processing lifecycle"
      description="Aggregate progress through Oracord Engine processing"
    >
      {error && <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {loading && !lifecycle && <p className="text-sm text-slate-500">Loading processing lifecycle…</p>}
      {lifecycle?.rows.length ? (
        <div className="divide-y divide-slate-100">
          {lifecycle.rows.map(row => (
            <div key={row.key} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
              <span className="text-sm font-medium text-slate-800">{ENGINE_LIFECYCLE_LABELS[row.key]}</span>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-sm font-semibold tabular-nums text-slate-700">{row.count}</span>
                <LifecycleStatusPill status={row.status} />
              </div>
            </div>
          ))}
        </div>
      ) : !loading ? (
        <p className="text-sm text-slate-500">Processing lifecycle status is unavailable.</p>
      ) : null}
      {lifecycle?.sourceHonesty && <p className="mt-4 text-xs text-slate-400">{lifecycle.sourceHonesty}</p>}
    </Card>
  );
}

function LifecycleStatusPill({ status }: { status: AdminEngineLifecycleStatusResponse['rows'][number]['status'] }) {
  const meta = {
    ready: { label: 'Ready', className: 'bg-emerald-50 text-emerald-700' },
    in_progress: { label: 'In progress', className: 'bg-blue-50 text-blue-700' },
    needs_setup: { label: 'Needs setup', className: 'bg-slate-100 text-slate-600' },
  }[status];

  return <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>;
}

function EngineStatePill({ label, tone }: { label: string; tone: 'green' | 'amber' | 'slate' }) {
  const classes = {
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    slate: 'bg-slate-100 text-slate-600',
  }[tone];

  return <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${classes}`}>{label}</span>;
}

function formatPairingExpiry(expiresAt: string) {
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return expiresAt;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function EngineStatusPill({
  status,
  label,
}: {
  status: AdminLocalProcessorSettingsResponse['engine']['status'];
  label: AdminLocalProcessorSettingsResponse['engine']['statusLabel'];
}) {
  const classes = {
    ready: 'bg-emerald-50 text-emerald-700',
    needs_attention: 'bg-amber-50 text-amber-700',
    not_downloaded: 'bg-slate-100 text-slate-600',
  }[status];

  return <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${classes}`}>{label}</span>;
}

function EngineCheckPill({ status }: { status: AdminLocalProcessorSettingsResponse['engine']['checks'][number]['status'] }) {
  const meta = {
    ready: { label: 'Ready', className: 'bg-emerald-50 text-emerald-700' },
    needs_attention: { label: 'Needs attention', className: 'bg-amber-50 text-amber-700' },
    not_ready: { label: 'Not ready', className: 'bg-slate-100 text-slate-600' },
  }[status];

  return <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>;
}

function BusinessLineAssignmentsCard({
  assignments,
  staffProfiles,
  loading,
  error,
  canManage,
  pendingPhoneNumber,
  onAssign,
}: {
  assignments: AdminBusinessLineAssignmentsResponse | null;
  staffProfiles: AdminStaffProfilesResponse['rows'];
  loading: boolean;
  error: string | null;
  canManage: boolean;
  pendingPhoneNumber: string | null;
  onAssign: (phoneNumber: string, staffProfileId: string | null) => void;
}) {
  if (loading && !assignments) {
    return <p className="text-sm text-slate-500">Loading business lines…</p>;
  }

  if (!assignments || assignments.rows.length === 0) {
    return (
      <div className="space-y-3">
        {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <EmptyPanel title="No business lines yet" description="Business lines will appear here after they are added." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <div className="divide-y divide-slate-100">
        {assignments.rows.map(row => {
          const value = row.assignedStaffProfile?.id ?? '';
          const pending = pendingPhoneNumber === row.phoneNumber;
          return (
            <div key={row.phoneNumber} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-medium text-slate-950">{row.phoneNumber}</div>
                <div className="mt-1 text-sm text-slate-500">
                  {row.assignedStaffProfile ? `Assigned to ${row.assignedStaffProfile.displayName || row.assignedStaffProfile.staffIdentity}` : 'Needs assignment'}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${row.assignmentStatus === 'assigned' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {row.assignmentStatus === 'assigned' ? 'Assigned' : 'Needs assignment'}
                </span>
                <select
                  value={value}
                  disabled={!canManage || pending || staffProfiles.length === 0}
                  onChange={event => onAssign(row.phoneNumber, event.target.value || null)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label={`Assign ${row.phoneNumber} to staff profile`}
                >
                  <option value="">Unassigned</option>
                  {staffProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.displayName || profile.staffIdentity}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">{assignments.sourceHonesty}</p>
      <p className="text-xs text-slate-400">{assignments.sharedLineSupport.policy}</p>
    </div>
  );
}

function QuietAction({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-white hover:text-slate-950"
    >
      {children}
    </button>
  );
}

function SettingToggleRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div>
        <div className="font-medium text-slate-950">{title}</div>
        <div className="mt-1 text-sm text-slate-500">{description}</div>
      </div>
      <Toggle checked={checked} disabled={disabled} onChange={onChange} />
    </div>
  );
}

function InfoRow({ title, description }: { title: string; description: string }) {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="font-medium text-slate-950">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{description}</div>
    </div>
  );
}

function Toggle({ checked, disabled, onChange }: { checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  const tone: ToggleTone = checked ? 'blue' : 'slate';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tone === 'blue' ? 'bg-blue-600' : 'bg-slate-200'
      }`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition ${checked ? 'left-6' : 'left-1'}`}
      />
    </button>
  );
}

function StatusText({ children, tone }: { children: React.ReactNode; tone: 'green' | 'slate' | 'red' }) {
  const classes = {
    green: 'text-emerald-600',
    slate: 'text-slate-500',
    red: 'text-red-600',
  }[tone];
  return <div className={`text-sm font-medium ${classes}`}>{children}</div>;
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-6 text-center">
      <div className="text-sm font-medium text-slate-800">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{description}</div>
    </div>
  );
}

function ConnectedList({ rows, empty }: { rows: string[]; empty: string }) {
  if (rows.length === 0) return <EmptyPanel title={empty} description="You can add more later." />;
  return (
    <div className="divide-y divide-slate-100">
      {rows.map(row => (
        <div key={row} className="flex items-center justify-between py-3">
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">{row}</span>
          <button type="button" className="text-sm font-medium text-red-600">Disconnect</button>
        </div>
      ))}
    </div>
  );
}

function SelectRow({ title, description, value }: { title: string; description: string; value: string }) {
  return (
    <div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="font-medium text-slate-950">{title}</div>
        <div className="mt-1 text-sm text-slate-500">{description}</div>
      </div>
      <select value={value} onChange={() => undefined} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
        <option>{value}</option>
      </select>
    </div>
  );
}

function InputRow({
  id,
  title,
  description,
  value,
  placeholder,
  onChange,
}: {
  id: string;
  title: string;
  description: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const descriptionId = `${id}-description`;

  return (
    <div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <label htmlFor={id} className="font-medium text-slate-950">
          {title}
        </label>
        <div id={descriptionId} className="mt-1 text-sm text-slate-500">
          {description}
        </div>
      </div>
      <input
        id={id}
        type="email"
        multiple
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
        aria-describedby={descriptionId}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-400 sm:max-w-sm"
      />
    </div>
  );
}
